import { useEffect, useRef, useState } from 'react'
import { FiX } from 'react-icons/fi'

// ═══ PAINEL LATERAL DESLIZANTE ═══
//
// [2026-07-27] Extraído do padrão que era copiado à mão em LeaderFollowupPanel.tsx
// (a implementação de referência, citada como "padrão" no drawer de administração
// do App.tsx) e em WhatsAppInboxPanel.tsx. Três coisas que o padrão copiado NÃO
// tinha e que entram aqui:
//
// 1. ANIMAÇÃO DE VERDADE. Os drawers existentes carregam
//    `animate-in slide-in-from-right duration-200`, que são classes MORTAS:
//    `tailwindcss-animate` não está no package.json, `tailwind.config.js` tem
//    `plugins: []`, e não há nenhuma dessas classes no index.css nem no CSS
//    buildado. Ou seja, hoje os painéis aparecem e somem instantaneamente. Aqui a
//    animação é feita na mão com transition-transform + translate-x, sem
//    dependência nova, e a desmontagem é atrasada pra a saída ser visível.
//
// 2. ESCAPE e SCROLL LOCK. Nenhum drawer nem modal do projeto tem hoje.
//
// 3. FOOTER FIXO. O padrão existente empilha blocos com `border-b` acima do
//    miolo rolável; não havia caso de footer de ações preso no rodapé.
//
// ⚠️ ANCORAGEM DO NEU — o detalhe que faz este componente funcionar em qualquer
// lugar. `.neu-lab` é seletor ANCESTRAL (`:root:not(.dark) .neu-lab .neu-forms
// input`), e um painel pode ser aberto de dentro OU de fora da árvore que carrega
// essa classe. O AssignLeaderModal, por exemplo, é renderizado de 5 pontos, e em
// App.tsx ele fica FORA do `.neu-lab` (que abre na linha 965 e fecha na 1185) —
// então lá as classes neu-* seriam inertes e o mesmo painel apareceria sem estilo.
// Por isso o root aqui carrega `neu-lab` no próprio elemento: auto-ancora as
// variáveis (--neu-bg/dark/light) e faz os seletores descendentes casarem
// independente do ponto de montagem. `.neu-drawer` já é standalone por natureza.
const ANIM_MS = 200

interface SidePanelProps {
  isOpen: boolean
  onClose: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  /** Largura em desktop. Mobile é sempre w-full. Default: 640px. */
  width?: string
  /** Bloco de ações preso no rodapé, fora da área rolável. */
  footer?: React.ReactNode
  /** Some com o X do cabeçalho (quando o fluxo tem botão próprio de sair). */
  hideCloseButton?: boolean
  /** [2026-09-21] Clique no scrim fecha? Default true (comportamento histórico).
   *  Passe `false` em painel com FORMULÁRIO: clique fora leva junto tudo que a
   *  pessoa digitou. Nesses casos sai por ✕, Cancelar ou Esc. */
  closeOnScrim?: boolean
  children: React.ReactNode
}

export default function SidePanel({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  width = 'sm:w-[640px]',
  footer,
  hideCloseButton,
  closeOnScrim = true,
  children,
}: SidePanelProps) {
  // `render` mantém o painel montado durante a animação de saída; `shown`
  // dispara a transição no frame seguinte à montagem (sem o rAF o browser
  // pinta já na posição final e não há transição nenhuma).
  const [render, setRender] = useState(isOpen)
  const [shown, setShown] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isOpen) {
      if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
      setRender(true)
      const raf = requestAnimationFrame(() => setShown(true))
      return () => cancelAnimationFrame(raf)
    }
    setShown(false)
    closeTimer.current = setTimeout(() => setRender(false), ANIM_MS)
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current) }
  }, [isOpen])

  // Escape fecha. Registrado só enquanto aberto pra não competir com outros
  // overlays que possam estar por cima.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  // Trava o scroll do body. Guarda o valor anterior em vez de assumir '' —
  // se dois painéis se sobrepuserem, o de baixo restaura o estado certo.
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  if (!render) return null

  return (
    <>
      <div
        onClick={closeOnScrim ? onClose : undefined}
        className={`fixed inset-0 z-[9999998] glass-overlay transition-opacity duration-200 ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        role="dialog"
        aria-modal="true"
        // `top-14` abaixo de lg: no celular a barra do app (`header.lg:hidden`,
        // 56px) é pintada POR CIMA desta gaveta — o palco do CRM é um contêiner
        // com `z-index:10000`, então o z-[9999999] daqui só vale DENTRO dele e
        // não alcança a barra, que está fora. O efeito era o cabeçalho da
        // gaveta (nome da pessoa e o ✕ de fechar) invisível no telefone, em
        // TODOS os painéis. Ver a memória crm-sobreposicao-atras-do-z10000.
        className={`fixed top-14 lg:top-0 right-0 bottom-0 z-[9999999] w-full ${width} max-w-full
          glass-panel neu-lab neu-drawer flex flex-col
          transition-transform duration-200 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center gap-2.5 px-4 py-3 shrink-0 neu-drawer-head">
          {icon && (
            <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 neu-drawer-well">
              {icon}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">{title}</div>
            {subtitle && (
              <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>
            )}
          </div>
          {!hideCloseButton && (
            <button
              onClick={onClose}
              aria-label="Fechar"
              className="p-1.5 rounded-lg neu-drawer-x hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* neu-forms precisa ser DESCENDENTE de neu-lab, nao o mesmo elemento: a
            regra e  (index.css:1761),
            combinador descendente. Com as duas classes juntas no root o seletor
            nao casa e os campos ficam sem o poco — silenciosamente. */}
        <div className="flex-1 overflow-y-auto min-h-0 neu-forms">{children}</div>

        {footer && <div className="shrink-0 border-t border-border neu-forms">{footer}</div>}
      </div>
    </>
  )
}
